import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../lib/authStore';
import { ArrowLeft, User, Shield } from 'lucide-react';

interface MobileTopBarProps {
  title?: string;
  onOpenDrawer?: () => void;
  theme?: 'light' | 'dark';
}

export const MobileTopBar: React.FC<MobileTopBarProps> = ({
  title,
  onOpenDrawer,
  theme = 'light',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isStaff } = useAuth();
  const isDark = theme === 'dark';

  const isHome = location.pathname === '/' || location.pathname === '/client/dashboard' || location.pathname === '/admin';

  return (
    <header
      className={`md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-16 border-b backdrop-blur-md transition-all font-sans ${
        isDark
          ? 'bg-slate-950/95 border-slate-800 text-white'
          : 'bg-white/95 border-slate-200 text-slate-900 shadow-2xs'
      }`}
    >
      <div className="flex items-center gap-3 overflow-hidden">
        {!isHome ? (
          <button
            onClick={() => navigate(-1)}
            className={`p-2 -ml-1 rounded-full transition-colors active:scale-95 ${
              isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'
            }`}
            aria-label="Go Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <Link to="/" className="w-9 h-9 rounded-full overflow-hidden border border-brand-500/50 bg-white p-0.5 shrink-0 shadow-xs flex items-center justify-center">
            <img
              src="/ftt-logo.png"
              alt="For The Truth Logo"
              className="w-full h-full object-contain rounded-full"
            />
          </Link>
        )}

        <div className="flex flex-col overflow-hidden">
          <span className={`text-xs font-bold truncate leading-tight tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {title || 'FOR THE TRUTH PUBLISHING'}
          </span>
          <span className={`text-[10px] font-mono tracking-wide truncate ${isDark ? 'text-brand-400' : 'text-brand-700'}`}>
            Editorial House &bull; Literature
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {isAuthenticated ? (
          <Link
            to={isStaff ? '/admin' : '/client/dashboard'}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold font-mono transition-transform active:scale-95 ${
              isDark
                ? 'bg-brand-600 text-white ring-2 ring-brand-400/40'
                : 'bg-slate-900 text-white ring-2 ring-brand-500/40'
            }`}
            title="Profile & Dashboard"
          >
            {user?.firstName?.[0] || 'U'}
          </Link>
        ) : (
          <Link
            to="/login"
            className="px-3 py-1.5 rounded-full text-xs font-bold bg-brand-600 text-white shadow-xs active:scale-95"
          >
            Sign In
          </Link>
        )}
      </div>
    </header>
  );
};
